import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { calendarApi, type Booking } from '@/services/api'

type CalendarBookingsKey = readonly ['calendar-bookings', string, string, number | undefined]

/**
 * Les reserves més recents del cache per a una propietat, de qualsevol rang de dates.
 *
 * Fa falta per a `useQueries` (P7, `plans/calendar-visual-refresh.md` §7): quan la clau canvia (mes
 * nou → `from`/`to` nous), TanStack Query v5 hi crea un observer nou, sense dades d'abans, i el
 * `previous` de `placeholderData` arriba buit. Sense això la graella es buidava a cada canvi de mes.
 */
export function latestCachedBookings(
    queryClient: QueryClient,
    propertyId: number | undefined,
): Booking[] | undefined {
    let latest: { data: Booking[]; updatedAt: number } | undefined
    for (const query of queryClient.getQueryCache().findAll({ queryKey: ['calendar-bookings'] })) {
        if (query.queryKey[3] !== propertyId) continue
        const data = query.state.data
        if (!Array.isArray(data)) continue
        if (!latest || query.state.dataUpdatedAt > latest.updatedAt) {
            latest = { data: data as Booking[], updatedAt: query.state.dataUpdatedAt }
        }
    }
    return latest?.data
}

/**
 * Opcions de la query de reserves de la graella, per a `useQuery` i per a `useQueries` (el mode
 * «Totes» del calendari en fa una per propietat amb **la mateixa clau**, i el cache es comparteix).
 *
 * `placeholderData`: mentre arriba un rang nou de la **mateixa** propietat, es queden les reserves
 * d'abans (sense pantalla de càrrega en canviar de mes). D'una altra propietat, mai: la graella
 * ensenyaria les reserves d'una propietat sota la pestanya d'una altra. Amb `queryClient`, si
 * l'observer no en té d'abans (`useQueries`), les busca al cache (`latestCachedBookings`).
 */
export function calendarBookingsQueryOptions(
    from: string,
    to: string,
    propertyId?: number,
    queryClient?: QueryClient,
) {
    const queryKey: CalendarBookingsKey = ['calendar-bookings', from, to, propertyId]
    return {
        queryKey,
        queryFn: () => calendarApi.getBookings(from, to, propertyId),
        staleTime: 1000 * 60 * 5, // 5 minutes
        placeholderData: (
            previous: Booking[] | undefined,
            previousQuery?: { queryKey: readonly unknown[] },
        ) => {
            if (previous !== undefined && previousQuery?.queryKey[3] === propertyId) return previous
            return queryClient ? latestCachedBookings(queryClient, propertyId) : undefined
        },
    }
}

export function useCalendarBookings(from: string, to: string, propertyId?: number) {
    const queryClient = useQueryClient()
    return useQuery(calendarBookingsQueryOptions(from, to, propertyId, queryClient))
}
